import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cx } from './utils';

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
}

export function Field({ label, children, description, error, required = false, className }: FieldProps) {
  const descriptionId = useId();
  const errorId = useId();
  const childElement = isValidElement(children)
    ? children as ReactElement<Record<string, unknown>>
    : null;
  const describedBy = [
    childElement?.props['aria-describedby'],
    description ? descriptionId : null,
    error ? errorId : null,
  ].filter(Boolean).join(' ') || undefined;
  const control = childElement
    ? cloneElement(childElement, { 'aria-describedby': describedBy })
    : children;

  return (
    <label className={cx('ui-field', className)}>
      <span className="ui-field__label">
        {label}
        {required ? <span className="ui-field__required" aria-hidden="true">*</span> : null}
      </span>
      {control}
      {description ? (
        <span id={descriptionId} className="ui-field__description">
          {description}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="ui-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}
